import { Component, EventEmitter, Input, OnInit, OnChanges, Output, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  DynamicFormComponent,
  ModalComponent,
  Group,
  GroupsService,
  UtilsService,
  PopUpService,
  SubscriptionsService,
  ProfileService,
  Profile,
  JobsService
} from 'app/core';
import { GroupDataQuestionsService } from '../questions-services/group-data-questions.service';
import { TranslateService } from '@ngx-translate/core';
import { WARNING_BONUS_CLASS_USER } from '../../assets/constants';
import { LocalStorage } from '@models/localStorage';
import { Subscription } from 'rxjs';
import { DocumentsManagerService } from 'core/services/documents-manager.service';

@Component({
  selector: 'lms-group-data',
  templateUrl: './group-data.component.html',
  styleUrls: ['./group-data.component.scss'],
  providers: [GroupDataQuestionsService]
})
export class GroupDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() group: Group;
  @Output() save = new EventEmitter<Group>();
  @ViewChild('dateConfirmModal', { static: false }) public dateConfirmModal: ModalComponent;
  @ViewChild('bonusWarningModal', { static: false }) public bonusWarningModal: ModalComponent;
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;
  public data: any = {};
  public groupData: any = {};
  public modalChecked = false;
  public isCheckedBonusModal = false;
  private loggedUserId: string;
  private loggedUserSubscription: Subscription = new Subscription();
  private bonusCourseSubscription: Subscription = new Subscription();

  constructor(
    public route: ActivatedRoute,
    public groupsService: GroupsService,
    public subscriptionService: SubscriptionsService,
    public questionsService: GroupDataQuestionsService,
    public documentsManager: DocumentsManagerService,
    public popupService: PopUpService,
    public translate: TranslateService,
    private profileService: ProfileService,
    private jobsService: JobsService
  ) {}

  public ngOnInit(): void {
    this.route.data.subscribe((data) => {
      if (data.questionsDataScheme) {
        const tempData = UtilsService.getQuestionsData(this.route.snapshot, data.questionsDataScheme);
        this.data = { ...tempData, group: this.group };
      }
    });

    this.loggedUserSubscription = this.profileService.getLoggedUser$().subscribe((loggedUser: Profile) => {
      this.loggedUserId = loggedUser['id'];
    });

    this.checkBonusCourse();
  }

  private checkBonusCourse(): void {
    this.bonusCourseSubscription = this.questionsService.checkedBonusCourse$.subscribe((isBonus: boolean) => {
      const warningUserBonusModal: LocalStorage = JSON.parse(
        localStorage.getItem(`${WARNING_BONUS_CLASS_USER}_${this.loggedUserId}`)
      );

      if (isBonus && !warningUserBonusModal) {
        this.bonusWarningModal.open();
      } else if (isBonus && warningUserBonusModal && warningUserBonusModal['fieldName'] !== this.loggedUserId) {
        this.bonusWarningModal.open();
      }
    });
  }

  public ngOnChanges(): void {
    if (this.group) {
      this.setQuestionsData();
    }
  }

  public ngOnDestroy() {
    this.loggedUserSubscription.unsubscribe();
    this.bonusCourseSubscription.unsubscribe();
  }

  setQuestionsData() {
    this.data = { ...this.data, group: this.group };
  }

  submit(groupData: any) {
    groupData.flags = {
      ...(this.group?.flags || {}),
      showRanking: groupData.rankingConfig === 'showRanking',
      showAlias: groupData.rankingConfig === 'showAlias',
      obfuscateRanking: groupData.rankingConfig === 'obfuscateRanking'
    };
    delete groupData.rankingConfig;

    this.saveGroup(groupData);
  }

  openDateModal(groupData) {
    this.groupData = groupData;
    this.dateConfirmModal.open();
  }

  confirmModal() {
    this.modalChecked = true;
    this.dateConfirmModal.close();
    this.onUpdate(this.groupData);
  }

  closeModal() {
    this.dynamicForm.setSpinner(false);
    this.dateConfirmModal.close();
  }

  saveGroup(groupData: any) {
    const { documentation } = groupData;
    if (!documentation.hasChanges || !documentation.type) {
      delete groupData.documentation;
    }
    if (this.data.group) {
      this.onUpdate(groupData);
    } else {
      this.onCreate(groupData);
    }
  }

  onCreate(groupData: Object) {
    this.checkSubscriptionExpiryDate(groupData).then(() => {
      this.groupsService
        .create(groupData)
        .then((group) => {
          this.saveDocumentation(groupData, group);
          this.save.emit(group);
          this.endSubmit(null);
        })
        .catch((err) => {
          this.endSubmit(err);
        });
    });
  }

  onUpdate(updatedData: any) {
    const isBetween = this.isDateBetween(updatedData.startDate);
    if (isBetween && !this.modalChecked) {
      this.openDateModal(updatedData);
    } else {
      this.checkEndDateChanged(updatedData)
        .then(() => {
          const updatedgroup: Group = Object.assign({}, this.data.group, updatedData);
          this.groupsService
            .update(updatedgroup)
            .then((savedGroup) => {
              this.saveDocumentation(updatedData, savedGroup);
              this.save.emit(savedGroup);
              this.endSubmit(null);
              this.modalChecked = false;

              if (savedGroup.licenseExtensionJobId) {
                this.jobsService.manageJob(
                  savedGroup.licenseExtensionJobId,
                  this.jobsService.jobTypes.EXTEND_LICENSES_BULK
                );
              }
            })
            .catch((err) => {
              this.endSubmit(err);
            });
        })
        .catch(() => {
          this.save.emit(this.group);
          this.modalChecked = false;
        });
    }
  }

  checkEndDateChanged(updatedData) {
    return new Promise((resolve, reject) => {
      if (this.group.automatic) {
        return resolve(true);
      }
      const groupEndDate = new Date(this.group.endDate).getTime();
      const newEndDate = new Date(updatedData.endDate).getTime();
      if (newEndDate !== groupEndDate) {
        this.checkSubscriptionExpiryDate(updatedData)
          .then(() => {
            this.popupService
              .showConfirm({ type: 'warning', text: this.translate.instant('groups.warningChangeEndDate') })
              .then(() => resolve(true))
              .catch(() => reject());
          })
          .catch(() => reject());
      } else {
        return resolve(true);
      }
    });
  }

  checkSubscriptionExpiryDate(groupData): Promise<any> {
    const clientId = this.data.client?.id || this.data.group?.clientId;
    return new Promise((resolve, reject) => {
      this.subscriptionService
        .getClientSubscriptionExpiryDate(clientId)
        .then((res) => {
          if (res.expiryDate) {
            const title = this.translate.instant(
              res.subscriptionType === 'credits'
                ? 'groups.expirationInfo.creditsTitle'
                : 'groups.expirationInfo.subscriptionTitle'
            );
            const text = this.translate.instant(
              res.subscriptionType === 'credits'
                ? 'groups.expirationInfo.creditsText'
                : 'groups.expirationInfo.subscriptionText'
            );
            // TODO Check for automatic subscriptions if/when they are implemented
            if (groupData.endDate.toISOString() > res.expiryDate) {
              this.popupService
                .showConfirm({
                  title,
                  text,
                  type: 'info'
                })
                .then(() => resolve(true))
                .catch(() => reject());
            } else {
              return resolve(true);
            }
          } else {
            return resolve(true);
          }
        })
        .catch(() => reject());
    });
  }

  isDateBetween(startDate) {
    let isBetween = false;
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    const morning = new Date(year, month, day);
    const night = new Date(year, month, day, 23, 59, 59);
    const groupDate = new Date(startDate);
    if (groupDate >= morning && groupDate < night) {
      isBetween = true;
    }
    return isBetween;
  }

  saveDocumentation(groupData, savedGroup) {
    const { documentation = {} } = groupData;
    const { documentationUploadUrl } = savedGroup;
    if (documentation.hasChanges && documentation.type === 'pdf' && documentation._document && documentationUploadUrl) {
      this.documentsManager.uploadS3File(documentationUploadUrl, documentation._document).subscribe((res) => res);
    }
  }

  endSubmit(value) {
    this.dynamicForm.submitted(value);
  }

  /**
   * Close and save the option selected of the bonus modal
   * @param isAccepted
   */
  public closeBonusWarningModal(isAccepted: boolean): void {
    if (isAccepted && this.isCheckedBonusModal) {
      const warningUserBonusModal: LocalStorage = {
        fieldName: this.loggedUserId,
        isTrusted: isAccepted
      };
      localStorage.setItem(`${WARNING_BONUS_CLASS_USER}_${this.loggedUserId}`, JSON.stringify(warningUserBonusModal));
    }
    if (!isAccepted) {
      this.questionsService.setBonusQuestion(false);
    }
    this.bonusWarningModal.close();
  }

  /**
   * Events of checkbox of the bonus modal
   * @param isChecked
   */
  public onChange(isChecked: boolean): void {
    this.isCheckedBonusModal = isChecked;
  }
}
